import * as React from 'react';
import { colors } from '../tokens/colors';
import type { StatusKey } from '../tokens/colors';
import { cn } from '../lib/cn';

/**
 * StatusBadge — visual representation of order_status / line_status enums.
 *
 * Drives status visibility per Phase 1 §07. Always pairs color with a label
 * AND a leading dot (no color-only signal — passes a11y SC 1.4.1).
 *
 * The status keys come from packages/db enums; this component imports them
 * via @insolech/db at the consumer site to stay decoupled from the schema.
 *
 * RTL: dot is positioned via inline-start (logical) — works in both directions.
 */

const STATUS_LABELS: Record<StatusKey, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  in_review: 'In review',
  assigned_to_provider: 'Assigned',
  in_production: 'In production',
  quality_check: 'Quality check',
  in_delivery: 'In delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
  on_hold: 'On hold',
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusKey;
  /** Override label — typically passed from i18n (`t('order_status_in_production')`) */
  label?: string;
  size?: 'sm' | 'md';
  showDot?: boolean;
}

export function StatusBadge({
  status,
  label,
  size = 'md',
  showDot = true,
  className,
  ...props
}: StatusBadgeProps) {
  const colors_ = colors.status[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap select-none',
        'border border-current/10',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        className,
      )}
      style={{ backgroundColor: colors_.bg, color: colors_.fg }}
      {...props}
    >
      {showDot ? (
        <span
          aria-hidden="true"
          className="rounded-full"
          style={{
            backgroundColor: colors_.dot,
            width: size === 'sm' ? '6px' : '7px',
            height: size === 'sm' ? '6px' : '7px',
          }}
        />
      ) : null}
      {label ?? STATUS_LABELS[status]}
    </span>
  );
}
